import React, { useMemo, useCallback, useState } from "react";
import styles from "./ProgramItem.module.scss";

import IconArrowUp from "src/assets/icons/dropdown.svg?react";
import FlagAU from 'src/assets/icons/icon_au.svg?react';
import FlagCA from 'src/assets/icons/icon_ca.svg?react';
import FlagUS from 'src/assets/icons/icon_us.svg?react';

import Button from "@src/components/button/Button";
import { Collapse } from '@src/utils/componentCasts';
import { useHistory } from "react-router-dom";
import {
  SimpleMultiCollectionFragment,
  SllProgram,
  useRemoveMcFromProgramMutation,
} from "@src/api/graphql-codegen";
import { useToast } from "@src/hooks";

import ProgramOverview from "@src/components/program-overview/ProgramOverview";
import ProgramGrade from "@src/components/program-gradeItem/ProgramGrade";
import MultiCollectionModal from "../../multi-collections/modal/MultiCollectionModal";
import NormalModal from "@src/components/modal/normal-modal/NormalModal";
import { TextInput } from "@src/components/text-input/TextInput";
import { SelectOption } from "@src/types";
import AddMultiCollectionModal from "../modal/AddMultiCollectionModal";
import Spinner from "@src/components/spinner/Spinner";
import { sortByPosition } from "@src/utils/helpers";
import { logger } from '../../../../utils/logger';

type Props = {
  program: SllProgram;
  programId?: string;
  selectedId?: string | null;
  selected?: boolean;
  mcCollectionOptions?: SelectOption[] | [];
  mcId?: string;
  handleSeleted?: (selectId: string) => void;
  handleDelete?: (program: any) => void;
  refresh?: () => void;
};

const getFlagIcon = (country: String) => {
  switch (country) {
    case "Australia": {
      return <div style={{width: 40, height: 40}}><FlagAU width={40} /></div>;
    }
    case "Canada": {
      return <div style={{width: 40, height: 40}}><FlagCA width={40}/></div>;
    }
    case "USA": {
      return <div style={{width: 40, height: 40}}><FlagUS width={40}/></div>;
    }
    case "Canada & USA": {
      return (
        <>
          <div style={{width: 40, height: 40}}><FlagCA width={40}/></div>
          <div style={{width: 40, height: 40}}><FlagUS width={40}/></div>
        </>
      );
    }
    case "All Countries": {
      return <div><FlagAU width={40}/><FlagCA width={40}/><FlagUS width={40}/></div>;
    }
    default: {
      return null;
    }
  }
}

function ProgramItem(props: Props) {
  const {
    program,
    programId,
    selectedId,
    selected,
    mcCollectionOptions = [],
    handleSeleted,
    handleDelete,
    refresh,
  } = props;
  const history = useHistory();
  const { successToast, errorToast } = useToast();

  const [selectedMC, setSelectedMC] = useState<
    SimpleMultiCollectionFragment | undefined
  >(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMCEditModal, setShowMCEditModal] = useState(false);
  const [showMCDeleteModal, setShowMCDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState("");

  const [isSelectedMC, setIsSelectedMC] = useState(false);
  const [selectedMCId, setSelectedMCId] = useState<string | null>(null);

  const [removeMCFromProgram, {loading: removing}] = useRemoveMcFromProgramMutation({
    onCompleted(data, clientOptions) {
      if (data.removeMCFromProgram) {
        successToast("Multi Collection has been removed successfully!");
        refresh?.();
        setTimeout(() => setShowMCDeleteModal(false), 100)
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast("Remove failed, Please try again!")
    }
  })

  const isSelectedProgram = useMemo(() => {
    return selected && selectedId === programId ? true : false;
  }, [selected, selectedId, programId]);

  const handleSelectProgram = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSeleted?.(`${program.id}`);
  };

  // Handle Multi Collection
  const handleSelectMultiCollection = useCallback(
    (mcId: string) => {
      if (selectedMCId === mcId) {
        setIsSelectedMC(false);
        setSelectedMCId(null);
      } else {
        setIsSelectedMC(true);
        setSelectedMCId(mcId);
        setTimeout(() => {
          const courseElement = document.getElementById(`${program.id}-${mcId}`);
          courseElement?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 500);
      }
    },
    [program.id, selectedMCId]
  );

  const handleDeleteMultiCollection = useCallback(
    (mc: SimpleMultiCollectionFragment) => {
      setSelectedMC(mc);
      setShowMCDeleteModal(true);
    },
    []
  );

  const handleEditMutiCollection = useCallback(
    (mc: SimpleMultiCollectionFragment) => {
      setSelectedMC(mc);
      setShowMCEditModal(true);
    },
    []
  );

  const handleConfirmDeleteMultiCollection = useCallback(
    (mc: SimpleMultiCollectionFragment) => {
      setSelectedMC(mc);
      
      if (mc.id) {
        removeMCFromProgram({
          variables: {
            input: {
              mcId: mc.id,
              programId: program.id
            }
          }
        })  
      }
    },
    [program.id, removeMCFromProgram]
  );

  // Handle Programs
  const handleDeleteProgram = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleDelete?.(program);
    },
    [handleDelete, program]
  );

  const handleEditProgram = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      history.push({
        pathname: `/super/program-management/edit/${program.id}`,
        state: { programData: program },
      });
    },
    [history, program]
  );

  const mcCollections = useMemo(() => {
    if (!program.mcs) return [];
    return [...(program.mcs ?? [])]?.sort(sortByPosition);
  }, [program.mcs]);

  const handleClose = useCallback(() => {
    setShowMCEditModal(false);
    setShowMCDeleteModal(false);
    setShowAddModal(false);
    setSelectedMC(undefined);
  }, []);

  const handleAddMultiCollection = () => {
    setShowAddModal(true);
  }

  const handleAdded = () => {
    setShowAddModal(false);
    refresh?.();
    logger.info('Adding MCs....')
  }

  return (
    <div id={programId} className={styles.wrapper}>
      <div className={styles.programCard}>
        <div className={styles.titleSection} onClick={handleSelectProgram}>
          <div className={styles.overviewSection}>
            <IconArrowUp
              className={[
                styles.arrowIcon,
                isSelectedProgram
                  ? styles.materialToggleClose
                  : styles.materialToggleOpen,
              ].join(" ")}
              width={24}
              height={24}
            />
            <div style={{display: "flex", gap: '4px'}}>{getFlagIcon(`${program?.country}`)}</div>
            <h1>{program.name}</h1>
          </div>
          <div className={styles.action}>
            <Button
              outline={true}
              size="small"
              label="Delete"
              color="red"
              className={styles.deleteButton}
              onClick={handleDeleteProgram}
            />
            <Button
              outline={true}
              size="small"
              label="Edit"
              className={styles.editButton}
              onClick={handleEditProgram}
            />
          </div>
        </div>
        <Collapse isOpened={isSelectedProgram}>
          <div className={styles.collapseView}>
          {program.isShown && (
            <ProgramOverview program={program} isSuperAdmin={true} />
          )}
          {mcCollections && mcCollections.length > 0 ? (
            mcCollections?.map((mc) => {
              return (
                <ProgramGrade
                  programId={program.id}
                  multiCollection={mc as SimpleMultiCollectionFragment}
                  selected={isSelectedMC}
                  selectedId={selectedMCId}
                  handleSeleted={handleSelectMultiCollection}
                  handleDelete={handleDeleteMultiCollection}
                  handleEdit={handleEditMutiCollection}
                />
              );
            })
          ) : (
            <ProgramGrade
              multiCollection={undefined}
              selected={isSelectedMC}
              selectedId={selectedMCId}
              handleAddMCCollection={handleAddMultiCollection}
            />
          )}
          {mcCollections.length > 0 && <div className={styles.addCollectionWrapper}>
            <Button size="small" className={styles.addMCCollectionButton} label="+ Add Multi Collection" onClick={() => {setShowAddModal(true)}} />
          </div>}
          </div>
        </Collapse>
      </div>
      {showMCEditModal && selectedMC && (
        <MultiCollectionModal
          open={showMCEditModal}
          onClose={handleClose}
          collection={selectedMC}
        />
      )}
      {showMCDeleteModal && selectedMC && (
        <NormalModal
          className={styles.deleteModalWrapper}
          open={showMCDeleteModal}
          title={
            <div className={styles.deleteTitle}>Delete {selectedMC?.name}?</div>
          }
          description={
            <div className={styles.deletdDescription}>
              <h3>This can not be undone</h3>
              <TextInput
                id="delete-box"
                label={"Type DELETE"}
                value={deleteType}
                onChange={(v) => setDeleteType(v.toString())}
                placeholder="Type DELETE to confirm deletion"
              />
            </div>
          }
          leftButton={
            <Button
              size="small"
              className={styles.cancelButton}
              onClick={handleClose}
              label={"CANCEL"}
            />
          }
          rightButton={
            <Button
              disabled={deleteType !== "DELETE" || removing}
              size="small"
              className={styles.confirmDeleteButton}
              leftIcon={removing ? <Spinner width={20} height={20} /> : undefined}
              onClick={() => handleConfirmDeleteMultiCollection(selectedMC)}
              label={"CONFIRM DELETION"}
            />
          }
          onClose={handleClose}
        />
      )}
      {showAddModal && (
        <AddMultiCollectionModal open={showAddModal} mcCollectionOptions={mcCollectionOptions} program={program} onClose={handleClose} onAdded={handleAdded} />
      )}
    </div>
  );
}

export default ProgramItem;
